/**
 * SRES · Model Validation Step (Iter98 · Feb 2026).
 *
 * Step 15 of the 20-item wizard registry, surfaced under the 9-step
 * simplified flow at Stage 7 (Run Estimation). Presents an industry-
 * standard swath / slope-of-regression validation of the estimated
 * block model against the input drillhole composites:
 *
 *   • KPI summary card (bias · smoothing · slope · R²)
 *   • Three orthogonal swath plots (Easting / Northing / Elevation)
 *   • Slope-of-regression scatter
 *   • One-click download of the standalone Model Validation Report PDF
 *
 * All chart PNGs are streamed live from the backend endpoint
 * `GET /api/projects/{pid}/validation/charts/{axis}` so the frontend
 * never has to re-implement the analytics.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowClockwise, Download, WarningCircle, Info, ShieldCheck } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { api, API_BASE } from "@/lib/api";
import { WizardCard, LoadingBanner, StatusRow } from "@/pages/wizard/_shared";


// ────────────────────────────────────────────────────────────────
// Utilities
// ────────────────────────────────────────────────────────────────
const AXES = [
  { key: "easting",   label: "Easting (X)",   testid: "swath-chart-easting" },
  { key: "northing",  label: "Northing (Y)",  testid: "swath-chart-northing" },
  { key: "elevation", label: "Elevation (Z)", testid: "swath-chart-elevation" },
];

const VERDICT_MAP = {
  ready:              { tone: "ok",   label: "READY",              badge: "bg-emerald-100 text-emerald-800 ring-emerald-300" },
  bias_warning:       { tone: "warn", label: "BIAS WARNING",       badge: "bg-amber-100 text-amber-800 ring-amber-300" },
  slope_warning:      { tone: "warn", label: "SLOPE WARNING",      badge: "bg-amber-100 text-amber-800 ring-amber-300" },
  insufficient_data:  { tone: "info", label: "INSUFFICIENT DATA",  badge: "bg-slate-100 text-slate-700 ring-slate-300" },
};


function ChartTile({ projectId, axis, label, testid, refreshToken }) {
  const [status, setStatus] = useState("loading");
  const src = `${API_BASE}/projects/${projectId}/validation/charts/${axis}?t=${refreshToken}`;
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden"
         data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="text-[12px] font-semibold text-slate-700">{label} — Swath Plot</div>
        {status === "loading" && (
          <ArrowClockwise className="w-3.5 h-3.5 text-slate-400 animate-spin"
                           data-testid={`${testid}-spinner`} />
        )}
        {status === "error" && (
          <span className="text-[10px] text-rose-600 font-semibold">Failed to load</span>
        )}
      </div>
      <div className="relative bg-slate-50 flex items-center justify-center min-h-[160px]">
        {status === "error" ? (
          <div className="text-center px-4 py-6 text-slate-500 text-[12px]">
            <WarningCircle className="w-6 h-6 mx-auto text-slate-400 mb-1" />
            Chart unavailable — try re-running validation above.
          </div>
        ) : (
          <img
            src={src}
            alt={`${label} swath plot`}
            className="w-full h-auto"
            onLoad={() => setStatus("loaded")}
            onError={() => setStatus("error")}
            data-testid={`${testid}-img`}
            crossOrigin="use-credentials"
          />
        )}
      </div>
    </div>
  );
}


function KpiCard({ label, value, sub, tone = "info", testid }) {
  const toneCls = {
    ok:   "border-emerald-200 bg-emerald-50/40",
    warn: "border-amber-200 bg-amber-50/40",
    bad:  "border-rose-200 bg-rose-50/40",
    info: "border-slate-200 bg-slate-50/40",
  }[tone] || "border-slate-200 bg-slate-50/40";
  return (
    <div className={`rounded-md border ${toneCls} px-3 py-2 min-w-0`}
         data-testid={testid}>
      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide truncate">{label}</div>
      <div className="text-lg font-bold text-slate-900 leading-tight mt-0.5" data-testid={`${testid}-value`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────
export default function ModelValidationStep({ projectId }) {
  const qc = useQueryClient();
  const [refreshToken, setRefreshToken] = useState(() => Date.now());

  // Fetch validation JSON (auto-run on first visit if missing).
  const val = useQuery({
    queryKey: ["validation", projectId],
    queryFn: async () => {
      const r = await api.get(`/projects/${projectId}/p28/validation`);
      if (!r.data || (r.data.global && r.data.global.empty)) {
        // Empty — trigger a first-run pass.
        const p = await api.post(`/projects/${projectId}/p28/validation/run`, { n_bins: 12 });
        return p.data;
      }
      return r.data;
    },
    staleTime: 30_000,
    retry: 1,
  });

  const runMut = useMutation({
    mutationFn: (n_bins) => api.post(`/projects/${projectId}/p28/validation/run`, { n_bins }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["validation", projectId] });
      setRefreshToken(Date.now());
      toast.success("Model validation re-computed.");
    },
    onError: () => toast.error("Validation run failed — check server logs."),
  });

  const downloadMut = useMutation({
    mutationFn: async () => {
      const r = await api.post(`/projects/${projectId}/validation/report`, {}, { timeout: 90_000 });
      return r.data;
    },
    onSuccess: (data) => {
      const url = `${API_BASE}/projects/${projectId}/validation/report/${data.report_id}/download`;
      // Open in new tab so browser handles download prompt.
      window.open(url, "_blank");
      toast.success("Model Validation Report ready · download opened.");
    },
    onError: (e) => {
      const msg = e?.response?.data?.detail?.message
        || e?.response?.data?.message
        || "Report generation failed.";
      toast.error(msg);
    },
  });

  const v = val.data;
  const verdict = v?.verdict || "insufficient_data";
  const verdictMeta = VERDICT_MAP[verdict] || VERDICT_MAP.insufficient_data;
  const empty = !v || (v.global && v.global.empty);
  const g = v?.global || {};
  const s = v?.slope  || {};

  const biasTone = useMemo(() => {
    const b = g?.bias_percent;
    if (b === null || b === undefined) return "info";
    const a = Math.abs(b);
    return a <= 10 ? "ok" : a <= 25 ? "warn" : "bad";
  }, [g?.bias_percent]);

  const slopeTone = useMemo(() => {
    const sl = s?.slope;
    if (sl === null || sl === undefined) return "info";
    if (sl >= 0.9 && sl <= 1.1) return "ok";
    if (sl >= 0.75 && sl <= 1.25) return "warn";
    return "bad";
  }, [s?.slope]);

  const fmt = (x, d = 3) => (x === null || x === undefined ? "—" : Number(x).toFixed(d));

  return (
    <div className="space-y-4" data-testid="model-validation-step">
      {/* ── Header + actions ────────────────────────────────── */}
      <WizardCard
        title="Model Validation · Swath Plots"
        subtitle="Industry-standard swath / slope-of-regression check of the block model against composites"
        testId="model-validation-card"
        right={
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => runMut.mutate(12)}
              disabled={runMut.isPending}
              data-testid="validation-rerun-btn">
              <ArrowClockwise className={`w-3.5 h-3.5 mr-1.5 ${runMut.isPending ? "animate-spin" : ""}`} />
              {runMut.isPending ? "Running…" : "Re-run"}
            </Button>
            <Button
              size="sm"
              onClick={() => downloadMut.mutate()}
              disabled={empty || downloadMut.isPending}
              data-testid="validation-download-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {downloadMut.isPending ? "Compiling…" : "Download Validation Report"}
            </Button>
          </div>
        }>

        {val.isLoading && (
          <LoadingBanner>Computing model validation — this may take up to 30 seconds…</LoadingBanner>
        )}

        {val.isError && (
          <StatusRow tone="warn">
            <WarningCircle className="w-4 h-4 shrink-0" />
            Could not load validation. Ensure a block-model has been built and composites are available.
          </StatusRow>
        )}

        {!val.isLoading && empty && (
          <StatusRow tone="info">
            <Info className="w-4 h-4 shrink-0" />
            Validation not available yet — build the block model and load composite assays first, then click <b className="mx-1">Re-run</b>.
          </StatusRow>
        )}

        {!val.isLoading && !empty && (
          <>
            {/* Verdict + KPI grid */}
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-slate-500" />
              <div className="text-[12px] text-slate-600">Verdict</div>
              <span
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold ring-1 ${verdictMeta.badge}`}
                data-testid="validation-verdict-badge">
                {verdictMeta.label}
              </span>
              {v?.reasons?.[0] && (
                <div className="text-[11.5px] text-slate-600 truncate">· {v.reasons[0]}</div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KpiCard
                label="Global Bias" tone={biasTone}
                value={g?.bias_percent === undefined || g?.bias_percent === null ? "—" : `${g.bias_percent >= 0 ? "+" : ""}${g.bias_percent.toFixed(1)} %`}
                sub="block − comp / composite"
                testid="kpi-bias" />
              <KpiCard
                label="Smoothing Ratio"
                value={fmt(g?.smoothing_ratio, 2)}
                sub="σ_block ÷ σ_composite"
                testid="kpi-smoothing" />
              <KpiCard
                label="Slope of Regression" tone={slopeTone}
                value={fmt(s?.slope, 3)}
                sub={`target 1.00 · acceptable 0.75–1.25`}
                testid="kpi-slope" />
              <KpiCard
                label="R²"
                value={fmt(s?.r_squared, 3)}
                sub={`n = ${s?.n_points ?? 0} paired slabs`}
                testid="kpi-r2" />
            </div>

            {/* Swath charts grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
              {AXES.map((a) => (
                <ChartTile
                  key={a.key}
                  projectId={projectId}
                  axis={a.key}
                  label={a.label}
                  testid={a.testid}
                  refreshToken={refreshToken} />
              ))}
            </div>

            {/* Regression scatter */}
            <div className="pt-1">
              <ChartTile
                projectId={projectId}
                axis="regression"
                label="Slope of Regression"
                testid="swath-chart-regression"
                refreshToken={refreshToken} />
            </div>

            {/* Method note */}
            <div className="text-[11.5px] text-slate-500 leading-relaxed pt-1">
              <b>Method:</b> The domain is discretised into <b>{v?.n_bins ?? 12}</b> equal-width slabs along each
              principal axis. For every slab the mean composite grade and the mean block-model grade are
              computed. A slope of regression close to <b>1.00</b> with R² near <b>1.00</b> and a global bias
              within <b>±10 %</b> indicates the estimator is unbiased and appropriately smoothed for public
              reporting (JORC 2012 Table 1 §3 · CIM Best Practice 2019).
            </div>
          </>
        )}
      </WizardCard>
    </div>
  );
}
